import * as THREE from "three";

/**
 * Slow-drifting ambient particles in world-space. Adds density to the
 * dust the player flies through without being tied to ship motion or
 * the ring system. Cheap: a single pooled Points cloud with 80 nodes.
 *
 * Recycles particles behind the ship so the cloud stays roughly ahead
 * of the camera — no need to grow the pool for long runs.
 */

const COUNT = 80;
const SPAWN_AHEAD = 60;   // u in -z
const SPAWN_RANGE_Z = 80; // distribute across this z window
const SPAWN_LATERAL = 22;

export class VaporField {
  readonly points: THREE.Points;
  private positions: Float32Array;
  private velocities: Float32Array;
  private alphas: Float32Array;
  private geom: THREE.BufferGeometry;

  constructor() {
    this.positions = new Float32Array(COUNT * 3);
    this.velocities = new Float32Array(COUNT * 3);
    this.alphas = new Float32Array(COUNT);

    for (let i = 0; i < COUNT; i++) {
      this.respawn(i, new THREE.Vector3(0, 0, 0), true);
    }

    this.geom = new THREE.BufferGeometry();
    this.geom.setAttribute("position", new THREE.BufferAttribute(this.positions, 3));
    this.geom.setAttribute("alpha", new THREE.BufferAttribute(this.alphas, 1));

    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending,
      uniforms: {},
      vertexShader: `
        attribute float alpha;
        varying float vAlpha;
        void main() {
          vAlpha = alpha;
          vec4 mv = modelViewMatrix * vec4(position, 1.0);
          gl_PointSize = 24.0 * (1.0 / -mv.z) * 6.0;
          gl_Position = projectionMatrix * mv;
        }
      `,
      fragmentShader: `
        varying float vAlpha;
        void main() {
          vec2 c = gl_PointCoord - 0.5;
          float d = length(c);
          float a = smoothstep(0.5, 0.0, d) * vAlpha * 0.18;
          gl_FragColor = vec4(0.7, 0.85, 1.0, a);
        }
      `,
    });

    this.points = new THREE.Points(this.geom, mat);
    this.points.frustumCulled = false;
  }

  update(dt: number, shipPos: THREE.Vector3) {
    // Under reduced-motion, freeze the per-particle velocity so the cloud
    // reads as static dust rather than drifting motes. Recycling on
    // ship-pass still runs — without it the cloud would deplete.
    const reduced = typeof document !== "undefined" &&
      document.body.classList.contains("reduced-motion");
    for (let i = 0; i < COUNT; i++) {
      const ix = i * 3;
      if (!reduced) {
        this.positions[ix] += this.velocities[ix] * dt;
        this.positions[ix + 1] += this.velocities[ix + 1] * dt;
        this.positions[ix + 2] += this.velocities[ix + 2] * dt;
      }
      // Recycle when the ship is well past this particle.
      if (this.positions[ix + 2] > shipPos.z + 12) {
        this.respawn(i, shipPos);
      }
    }
    this.geom.attributes.position.needsUpdate = true;
  }

  private respawn(i: number, shipPos: THREE.Vector3, initial = false) {
    const ix = i * 3;
    const zOffset = initial
      ? -SPAWN_RANGE_Z * Math.random()
      : -SPAWN_AHEAD - SPAWN_RANGE_Z * Math.random() * 0.3;
    this.positions[ix] = shipPos.x + (Math.random() - 0.5) * SPAWN_LATERAL * 2;
    this.positions[ix + 1] = shipPos.y + (Math.random() - 0.5) * SPAWN_LATERAL;
    this.positions[ix + 2] = shipPos.z + zOffset;
    // Very slow random drift; mostly stationary in world space.
    this.velocities[ix] = (Math.random() - 0.5) * 0.4;
    this.velocities[ix + 1] = (Math.random() - 0.5) * 0.2;
    this.velocities[ix + 2] = (Math.random() - 0.5) * 0.2;
    this.alphas[i] = 0.5 + Math.random() * 0.5;
    if (this.geom?.attributes?.alpha) this.geom.attributes.alpha.needsUpdate = true;
  }
}
